import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  type CardProps,
  Button as ChakraButton,
  Flex,
  forwardRef,
  Grid,
  Image,
  type ImageProps,
  keyframes,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useTheme
} from '@chakra-ui/react'
import CustomTooltip from 'brokertslib/src/components/CustomTooltip'
import {
  CalculationStatus,
  type DiscountRequest,
  type InsurerProductPartAvailable,
  type OfferDiscounts,
  type OfferLeasing,
  OfferType,
  type OfferVehicle,
  type QuoteRating,
} from 'BrokerTsLib/src/dtos'
import { Insurer, Product } from 'BrokerTsLib/src/enums'
import { DiscountRequestState } from 'BrokerTsLib/src/enums/DiscountRequestState'
import * as global from 'brokertslib/src/helpers/global'
import { useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { formatDateTime } from 'BrokerTsLib/src/Utils/formatPrice'
import { type ChangeEvent, useCallback, useEffect, useState } from 'react'
import * as yup from 'yup'

import { useAuth } from '@/context/AuthContext'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { usePartner } from '@/context/PartnerContext'
import { usePrintContext } from '@/context/PrintContext'
import { Apps } from '@/dtos'
import { Check, Refresh } from '@/icons'
import { scrollToElementCenter } from '@/utils/scrollToElement'

import { type StepType, TourProvider, useTour } from '@reactour/tour'
import { Apis } from '../appSettings'
import { useOffersVehicleContext } from '../context/OffersVehicleProvider'
import { useUrl } from '../hooks'
import { useApp } from '../hooks/useApp'
import { useGetApiLogs, useGetOfferDocuments } from '../hooks/useDocuments'
import {
  useClone,
  useGetOfferAvailableDiscounts,
  useSaveOfferAddons,
  useSaveOfferDiscounts,
  useSetOfferPaymentFrequency,
} from '../hooks/useOffer'
import carBurstDuotone from '../images/car-burst-duotone.svg'
import CloneDuotoneReact from '../images/clone-duotone.svg?react'
import discountsActive from '../images/discounts-active.svg'
import discountsImage from '../images/discounts.svg'
import slidersDuotoneActive from '../images/sliders-duotone-active.svg'
import slidersDuotone from '../images/sliders-duotone.svg'
import { getQuoteId } from '../utils/getQuoteId'
import { CompanyLogo } from './CompanyLogo'
import { SelectedBadge } from './InsuranceQuoteItem/SelectedBadge'
import { Loading } from './Loading'
import { useLoadingOverlay } from './LoadingOverlay'
import { QuoteItemCkp } from './QuoteItem/QuoteItemCkp'
import { QuoteItemDiscountsForm } from './QuoteItem/QuoteItemDiscountsForm'
import { QuoteItemSettingsForm } from './QuoteItem/QuoteItemSettingsForm'
import { QuoteItemSidebar } from './QuoteItem/QuoteItemSidebar'
import { QuoteSummary } from './QuoteItem/QuoteSummary'
import { Stars } from './Stars'
import { BadgeGroup } from './ui/BadgeGroup'

const scale = keyframes`
0% { 
  transform: scale(1); 
}
50% { 
  transform: scale(1.05); 
}
100% { 
  transform: scale(1); 
}
`

export const scrollToOffer = (offerId: number | string) => {
  scrollToElementCenter(getQuoteId(offerId))
}

export const OfferStatus = {
  changed: 0,
  completed: 1,
  error: 2,
}
export type OfferStatus = (typeof OfferStatus)[keyof typeof OfferStatus]

export interface QuoteItemProps extends CardProps {
  offerId: number
  isSelected?: boolean
  onRatingChange?: (quoteId: number, rating: QuoteRating) => void
  onIncludedChange: (quoteId: number, included: boolean) => Promise<void>
  showTour?: boolean
}

interface TabContentProps {
  label: string
  image: string
  activeImage?: string
  showErrorBadge?: boolean
  isDisabled?: boolean
  disabledTooltip?: string
}

export const TabContent = ({
  label,
  image,
  activeImage,
  showErrorBadge,
  disabledTooltip,
  sx,
  isDisabled = false,
  ...imageProps
}: TabContentProps & ImageProps) => {
  return (
    <Tooltip
      label={
        isDisabled && disabledTooltip
          ? disabledTooltip
          : `${!activeImage ? 'Zobrazit' : 'Zavřít'
          } ${label.toLocaleLowerCase()}`
      }
    >
      <Text>
        <Image
          src={activeImage ? activeImage : image}
          alt={label}
          {...imageProps}
          sx={{
            ...sx,
            opacity: isDisabled ? 0.5 : 1,
          }}
        />

        {showErrorBadge && <ErrBadge />}
      </Text>
    </Tooltip>
  )
}

const ErrBadge = () => {
  return (
    <Badge
      sx={{
        position: 'relative',
        top: -2,
        left: -2,
        boxShadow: 'md',
        backgroundColor: 'red',
        color: 'white',
      }}
    >
      i
    </Badge>
  )
}

export const QuoteItem = forwardRef((props: QuoteItemProps, ref) => {
  if (props.showTour) {
    const steps: StepType[] = [
      {
        selector: '[data-tour="quote-item"]',
        position: ({ left, width, bottom, }) => [left + width / 2, bottom + 50],
        content: () => (
          <Box>
            <Text fontWeight="bold" mb={2}>
              Nabídka pojištění
            </Text>
            <Text mt={4}>
              Zde vidíte již konkrétní nabídku pojišťovny. <br />
              U nabídky můžete měnit připojištění a frekvenci plateb.
            </Text>
          </Box >
        )
      },
      {
        selector: '[data-tour="quote-item-addons"]',
        position: ({ left, width, bottom, }) => [left - 100, bottom + 30],
        // action: (elem) => (elem as HTMLElement | null)?.click(),
        content: () => (
          <Box>
            <Text fontWeight="bold" mb={2}>
              Možnosti připojištění
            </Text>
            <Text mt={4}>
              Kliknutím na ikonu zobrazíte možnosti připojištění pro konkrétní nabídku
            </Text>
          </Box >
        ),
      },
      {
        selector: '[data-tour="quote-item-product"]',
        position: ({ left, width, bottom, }) => [left - 15, bottom + 30],
        content: ({ setIsOpen }) => (
          <Stack>
            <Text fontWeight="bold" mb={2}>
              Podrobnosti produktu
            </Text>
            <Text mt={4}>
              Najetím myší, nebo kliknutím zobrazíte podrobnosti o produktu
            </Text>
          </Stack >
        ),

      }
    ]

    return (
      <TourProvider
        steps={steps}
        // badgeContent={({ currentStep, totalSteps }) => `${currentStep + 1}/${totalSteps}`}
        padding={{
          mask: 25
        }}
        prevButton={({ Button, currentStep }) => {
          if (currentStep === 0) return <Box w={3}></Box>
          return <Button kind="prev" />
        }}
        nextButton={({ Button, currentStep, stepsLength, setIsOpen }) => {
          if (currentStep === stepsLength - 1)
            return <ChakraButton onClick={() => setIsOpen(false)}>Ukončit</ChakraButton>

          return <Button kind="next" />
        }}
        styles={{
          maskWrapper: (base) => ({
            ...base,
            zIndex: 1500
          }),
          maskArea: (base) => ({
            ...base,
            rx: 12,
            ry: 12,
          }),
          highlightedArea: (base) => ({
            ...base,
            rx: 12,
            ry: 12,
            zIndex: 1600,
          }),
          popover: (base) => ({
            ...base,
            padding: '16px 20px',
            borderRadius: 12,
          }),
          controls: (base) => ({
            ...base,
            marginTop: 32,
          }),
          badge: (base) => ({
            ...base,
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }),
          button: (base) => ({
            ...base,
            padding: '12px',   // zvětší klikací plochu
          }),

        }}
      >
        <QuoteItemContent {...props} ref={ref} />
      </TourProvider>
    )
  }

  return <QuoteItemContent {...props} ref={ref} />
})

const QuoteItemContent = forwardRef(
  (
    {
      offerId,
      isSelected,
      onRatingChange,
      onIncludedChange,
      showTour = false,
      ...rest
    }: QuoteItemProps,
    ref,
  ) => {
    const { product, app } = useApp()
    const isPublicPoleas = app == Apps.publicPoleas
    const { printMode } = usePrintContext()
    const { isDisabled, insuranceParams, insuranceParamsId } = useInsuranceParams()
    const {
      updateOffer,
      showAll,
      calculatingOffersIds,
      recalculateOffers,
      offers,
    } = useOffersVehicleContext()

    const { setIsOpen, isOpen, steps: tourSteps, currentStep } = useTour()

    // const [seen, setSeen] = useState(false)
    useEffect(() => {
      if (!showTour) return;

      const seen = localStorage.getItem('quoteItemTour_seen')
      if (!seen) {
        // setSeen(true)

        // timeout aby se pockalo na dokonceni scroll animace
        setTimeout(() => {
          setIsOpen(true)
        }, 1000)
        localStorage.setItem('quoteItemTour_seen', '1')
      }
    }, [showTour])

    const isContracted = !!insuranceParams?.generalParams.contractNumber
    const isContractedOffer = insuranceParams?.generalParams.selectedOfferId == offerId && isContracted
    const disabledSwitchIncludeButton = isDisabled && !(app == Apps.leasing && !isContracted)
    const offer = offers.find((x) => x.offerId == offerId)!
    const isImported = (offer as OfferLeasing)?.imported ?? false

    const { loading, setLoading, startAnimation } = useLoadingOverlay()

    const isRecalculating =
      calculatingOffersIds?.includes(offer!.offerId) ?? false
    const { user } = useAuth()
    const { currentPartner, getBaseUrl } = usePartner()
    const allowDiscountsOverLimit = user?.allowDiscountsOverLimit ?? false
    const documentUrl = useUrl(Apis.document)

    const elementId = getQuoteId(offer.offerId)
    const summaryTabIndex = 2
    const [tabIndex, setTabIndex] = useState(summaryTabIndex)

    const offerLeasing = offer as OfferLeasing

    const hasError = (offer.errors?.length ?? 0) > 0
    const [currentStatus, setCurrentStatus] = useState<OfferStatus>(
      hasError ? OfferStatus.error : OfferStatus.completed,
    )
    const [transitioningOut, setTransitioningOut] = useState(false)

    const { mutateAsync: saveAddons } = useSaveOfferAddons(offer.offerId)
    const { mutateAsync: saveDiscounts } = useSaveOfferDiscounts()
    const { mutateAsync: updatePaymentFrequency } =
      useSetOfferPaymentFrequency()

    const [changedPaymentFrequencyId, setChangedPaymentFrequencyId] = useState<
      number | undefined
    >(undefined)
    const [addons, setAddons] = useState<any>({})
    const [requestDiscountData, setRequestDiscountData] =
      useState<DiscountRequest>({
        state: DiscountRequestState.None,
        userCanApprove: false,
        discounts: [],
      })

    const { data: allApiLogs } = useGetApiLogs(insuranceParamsId!, { retry: false, enabled: !isPublicPoleas })
    const apiLogs = allApiLogs?.data.filter(
      (x) => x.insurerOfferId.includes(offer.offerId),
    )

    const {
      refetch: getOfferDocuments,
      data: offerDocuments,
    } = useGetOfferDocuments(offer!.offerId, { enabled: false, retry: false })
    const xmlDocs = offerDocuments?.data.filter(
      (x) => x.name !== 'OfferDocument',
    )

    const { mutateAsync: cloneOffer } = useClone(offer.offerId)

    const discountsFormMethods = useForm<OfferDiscounts>({
      resolver: yupResolver(yup.object().shape({})),
      mode: 'onBlur',
      defaultValues: {
        discounts: [],
      },
    })

    const { refetch: reloadDiscounts, isLoading: isLoadingDiscounts } =
      useGetOfferAvailableDiscounts(offer.offerId, {
        enabled: !isPublicPoleas,
        onSuccess(data) {
          // po kazdem prenacteni slev se musi resetovat formular.
          discountsFormMethods.reset({
            discounts: data.data.availableDiscounts,
          })

          if (allowDiscountsOverLimit && data.data.discountRequest) {
            setRequestDiscountData(data.data.discountRequest)
          }
        },
      })

    // doctu nove dokumenty po kazde rekalkulaci
    useEffect(() => {
      if (!isRecalculating) {
        if (!isPublicPoleas)
          reloadDiscounts() // potreba refreshnout slevy, protoze se po kalkulaci mohly zmenit (napr. i v pripade pouziti a schvalovani nadlimitnich slev)

        setCurrentStatus(hasError ? OfferStatus.error : OfferStatus.completed)

        if (user?.enableOfferXml) {
          // aby docitani dokumentu nebrzdilo jine dulezitejsi requesty. Do budoucna vracet bud vramci offery uz, nebo volat pres connection signalR
          setTimeout(() => getOfferDocuments(), 2000)
        }

        setLoading(false)
      } else {
        setLoading(true)
      }
    }, [isRecalculating])

    const resetAddons = useCallback(() => {
      const defaultValues: any = {}

      offer.parts
        ?.filter((x) => x.riskType == 3)
        .forEach((x) => {
          defaultValues[x.riskId] = {
            riskId: x.riskId,
            insurerProductId: x.productId,
            insurerInternalId: x.insurerInternalId,
          }
        })

      setAddons({ ...defaultValues })
    }, [offer.parts])

    // nastaveni doplnku do state
    useEffect(() => {
      resetAddons()
    }, [offer.parts.length])

    const updateAddons = (addons: any) => {
      handleStatusChange(OfferStatus.changed)
      setAddons(addons)
    }

    useEffect(() => {
      if (discountsFormMethods.formState.isDirty)
        handleStatusChange(OfferStatus.changed)
    }, [discountsFormMethods.formState.isDirty])

    const recalc = (force: boolean) => {
      setLoading(true)
      return recalculateOffers([offer.offerId], force)
        .then(({ data }) => {
          handleStatusChange(
            !data[0].price ||
              data[0].errors.filter((x) => !x.softError).length > 0
              ? OfferStatus.error
              : OfferStatus.completed,
          )
        })
        .catch(() => handleStatusChange(OfferStatus.error))
        .finally(() => {
          setTimeout(() => {
            // aby se na offeru nascrolovalo pokud by napr. doslo ke zmene poradi kvuli zmeny ceny
            scrollToOffer(offer.offerId)

            setTimeout(() => {
              startAnimation()
            }, 500)
          }, 100)
        })
    }

    const submit = () => {
      setLoading(true)

      return new Promise((resolve, reject) => {
        Promise.all([
          submitAddons(),
          submitDiscounts(),
          submitChangedPaymentFrequency(),
        ])
          .then(() => {
            setTabIndex(summaryTabIndex)

            recalc(true)
              .then(() => {
                resolve(true)
              })
              .catch((err) => {
                reject(err)
              })
              .finally(() => setLoading(false))
          })
          .catch((err) => {
            reject(err)
            setLoading(false)
          })
      })
    }

    const submitDiscounts = discountsFormMethods.handleSubmit((vals) => {
      if (isPublicPoleas) return Promise.resolve(vals)

      return new Promise((resolve, reject) => {
        saveDiscounts({ data: vals, offerId: offer.offerId })
          .then((response) => {
            discountsFormMethods.reset(vals)
            resolve(vals)
          })
          .catch((err) => {
            reject(err ?? null)
            if (
              !(
                err.response &&
                err.response.status === 400 &&
                err.response.data
              )
            )
              return

            const data = err.response.data
            // zobrazeni serverove validace
            Object.keys(data).forEach((x: any) => {
              discountsFormMethods.setError(global.toCamelCase(x), {
                message: data[x].join(' '),
              })
            })
          })
      })
    })

    const submitChangedPaymentFrequency = () => {
      if (
        !changedPaymentFrequencyId ||
        changedPaymentFrequencyId == offer.paymentFrequencyId
      )
        return Promise.resolve()

      return updatePaymentFrequency({
        offerId: offer.offerId,
        paymentFrequency: changedPaymentFrequencyId,
      })
    }

    const submitAddons = () => {
      const arr: InsurerProductPartAvailable[] = []
      Object.keys(addons).forEach((x) => arr.push(addons[x]))
      return saveAddons(arr)
    }

    const handleTabsChange = (index: number) => {
      setTabIndex(index)
    }

    const handleStatusChange = useCallback((status: OfferStatus) => {
      setCurrentStatus(status)
    }, [])

    // NOTE: Použít useTransition v React 19 pro disable switche než se provede změna.
    const handleQuoteVisibleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const target = e.currentTarget as HTMLInputElement
      const isVisible = target.checked

      if (!showAll) setTransitioningOut(!isVisible)

      return onIncludedChange(offer.offerId, isVisible)
    }

    let discountRequestColorScheme = ''
    let discountRequestTooltip = ''
    if (offer?.discountRequest) {
      switch (offer.discountRequest.state) {
        case DiscountRequestState.Requested:
          discountRequestColorScheme = 'orange'
          discountRequestTooltip =
            'Zažádáno o nadlimitní slevu. Čeká se na schválení.'
          break
        case DiscountRequestState.Approved:
          discountRequestColorScheme = 'green'
          discountRequestTooltip =
            'Žádost o nadlimitní slevu byla úspěšně schválena.'
          break
        case DiscountRequestState.Declined:
          discountRequestColorScheme = 'red'
          discountRequestTooltip = 'Žádost o nadlimitní slevu byla zamítnuta.'
          break
      }
    }

    let settingTooltip = 'Nastavení kalkulace'
    if (currentPartner?.partner == 'Jap') {
      // chtějí jiný popisek
      settingTooltip = 'Doplňkové pojištění'
    }

    const exludedFromPrint = !offer.print
    const theme = useTheme()
    const disableRecaclulate = offer.state == CalculationStatus.Policy
    const isInCalculationState = offer.state == CalculationStatus.Calculation

    const infos = [...(offer?.infos ?? []), ...(offer?.warnings ?? [])]
    let boxShadow = 'var(--card-shadow)'
    if (printMode && !exludedFromPrint) {
      boxShadow = `0px 0px 8px -2px ${theme.colors.green[600]}`
    } else if (offer.selected) {
      boxShadow = `0px 0px 8px -2px ${theme.colors.blue[600]}`
    }

    return (
      <Card
        ref={ref}
        id={elementId}
        onClick={(e) => {
          if (!printMode) return

          offer.print = !offer.print
          updateOffer(offer)
        }}
        sx={{
          height: transitioningOut ? 0 : '100%',
          opacity: transitioningOut ? 0 : 1,
          transition: 'all 300ms ease-out',
          cursor: printMode ? 'pointer' : undefined,
          boxShadow: boxShadow,
        }}
        data-tour={showTour ? 'quote-item' : undefined}
        {...rest}
      >
        <CardHeader sx={{ position: 'relative', px: 0 }}>
          <Flex
            sx={{
              position: 'absolute',
              left: -2,
              top: -2,
              gap: 2,
              zIndex: '100', // pridano kvuli prekryti loading boxem
            }}
          >
            {printMode && !exludedFromPrint && (
              <Badge
                colorScheme="green"
                variant="solid"
                sx={{
                  boxShadow: 'md',
                  transform: 'rotate(-3deg)',
                }}
                {...rest}
              >
                <CustomTooltip label="Zahrnuto k tisku">
                  <Check />
                </CustomTooltip>
              </Badge>
            )}

            {(isSelected || offer.selected) && !isContractedOffer && !printMode && (
              <SelectedBadge
                sx={{
                  transform: 'rotate(-3deg)',
                  boxShadow: 'md',
                }}
                bg="badge-bg"
              />
            )}

            {isImported && !printMode && (
              <SelectedBadge
                sx={{
                  boxShadow: 'md',
                  transform: 'rotate(-3deg)',
                }}
                text="Importováno"
                colorScheme="gray"
              />
            )}

            {isContractedOffer && !printMode && (
              <SelectedBadge
                sx={{
                  boxShadow: 'md',
                  transform: 'rotate(-3deg)',
                }}
                text="Sjednaná"
                colorScheme="green"
              />
            )}

            {offer.type == OfferType.KoopSalon && (
              <SelectedBadge
                sx={{
                  boxShadow: 'md',
                  transform: 'rotate(-3deg)',
                }}
                text="KoopSalon"
              // colorScheme="gray"
              />
            )}
          </Flex>

          <Grid
            gridTemplateColumns="1fr 17rem"
            sx={{
              gap: {
                base: 4,
                md: 8,
              },
              // flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Flex
              flex="1"
              sx={{
                gap: {
                  base: 4,
                  md: 8,
                },
                pl: { base: 4, lg: 8 },
                // flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <CompanyLogo
                insurer={{ id: offer.insurerId, name: offer.insurerNameFull }}
                width="3.75rem"
                height="2.5rem"
                offerType={offer.type}
              />

              <Text sx={{ fontWeight: 'bold' }}>{offer.insurerNameFull}</Text>

              {user?.showOffersRating && offers.length > 1 && (
                <Stars
                  rating={offer.stars}
                  onChange={(rating) => {
                    onRatingChange?.(offer.offerId, rating)
                  }}
                />
              )}

              {product == Product.VehicleLeasingInsurance && (
                <BadgeGroup
                  items={[
                    {
                      label: 'float',
                      color: 'primary.500',
                      isActive: !offerLeasing.fixedPrice,
                    },
                    {
                      label: 'fix',
                      color: 'black',
                      isActive: offerLeasing.fixedPrice,
                    },
                  ]}
                />
              )}

              {offer?.discountRequest && !printMode && (
                <Tooltip label={discountRequestTooltip}>
                  <Badge colorScheme={discountRequestColorScheme}>
                    <Text sx={{ fontWeight: 'bold' }}>Nadlimitní slevy</Text>
                  </Badge>
                </Tooltip>
              )}

              {(infos.length > 0 ||
                (offer?.underwriting?.required ?? false)) &&
                !printMode && (
                  <CustomTooltip
                    hasArrow
                    trigger="clickHover"
                    label={
                      <Stack direction={'column'}>
                        {offer?.underwriting?.required &&
                          offer?.underwriting?.message && (
                            <Text
                              key={-1}
                              mx={1}
                              my={1}
                              fontSize={'sm'}
                              whiteSpace={'break-spaces'}
                              dangerouslySetInnerHTML={{ __html: offer.underwriting.message ?? '' }}
                            />
                          )}
                        {infos.map((e, i) => {
                          return (
                            <Text
                              key={i}
                              mx={1}
                              my={1}
                              fontSize={'sm'}
                              whiteSpace={'break-spaces'}
                              dangerouslySetInnerHTML={{ __html: e.message ?? '' }}
                            />
                          )
                        })}
                      </Stack>
                    }
                    maxW='2xl'
                  >
                    <Badge colorScheme="yellow" variant="solid">
                      <Text cursor="pointer" color="white">
                        Info
                      </Text>
                    </Badge>
                  </CustomTooltip>
                )}

              {offer?.errors && offer?.errors.length > 0 && (
                <CustomTooltip
                  hasArrow
                  trigger="clickHover"
                  label={
                    <Stack direction={'column'}>
                      {offer?.errors.map((e, i) => {
                        return (
                          <Text
                            key={i}
                            mx={2}
                            my={1}
                            fontSize={'sm'}
                            variant="calcError"
                            whiteSpace={'break-spaces'}
                            dangerouslySetInnerHTML={{ __html: e.message ?? '' }}
                          />
                        )
                      })}
                    </Stack>
                  }
                  maxW='2xl'
                >
                  <Badge colorScheme="red">
                    <Text cursor="pointer" fontWeight="bold" color="red.800">
                      Chyba kalkulace
                    </Text>
                  </Badge>
                </CustomTooltip>
              )}

              <Flex ml="auto" gap={2}>
                {xmlDocs &&
                  xmlDocs.length > 0 &&
                  user?.enableOfferXml &&
                  !printMode && (
                    <Menu size="sm" placement="bottom">
                      <MenuButton
                        sx={{ gap: 2, whiteSpace: 'nowrap' }}
                        type="button"
                      >
                        XML
                      </MenuButton>
                      <MenuList>
                        {xmlDocs.map((doc) => (
                          <MenuItem
                            key={doc.id}
                            as={'a'}
                            target="_blank"
                            href={`${documentUrl}/GetOfferDocument?docId=${doc.id}`}
                          >
                            {doc.name} (
                            {formatDateTime(
                              doc.date,
                              'D.M.YYYY HH:mm:ss.SSS',
                            )}
                            )
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  )}

                {apiLogs &&
                  apiLogs.length > 0 &&
                  user?.enableOfferXml &&
                  !printMode && (
                    <Menu size="sm" placement="bottom">
                      <MenuButton
                        sx={{ gap: 2, whiteSpace: 'nowrap' }}
                        type="button"
                      >
                        ApiLog
                      </MenuButton>
                      <MenuList>
                        {apiLogs.map((doc) => (
                          <MenuItem
                            key={doc.id}
                            as={'a'}
                            href={`${getBaseUrl()}/apilog/${doc.id}`}
                            target='_blank'
                          >
                            {doc.action} (
                            {formatDateTime(
                              new Date(doc.requestUtc),
                              'D.M.YYYY HH:mm:ss.SSS',
                            )}
                            ) - {doc.statusCode}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  )}

                {user?.enableRecalculateOffer &&
                  !isDisabled &&
                  !printMode &&
                  !disableRecaclulate && (
                    <Image
                      as={Refresh}
                      onClick={() => {
                        setLoading(true)
                        recalc(true)
                      }}
                      cursor="pointer"
                    />
                  )}
              </Flex>
            </Flex>

            {/* mezery mezi itemy jsou 24px. Buttony s ikonama uz maji ale paddingy, tak proto gap pouze 3 (12px) */}
            <Flex
              sx={{
                alignItems: 'center',
                justifyContent: !printMode ? (isPublicPoleas ? 'space-between' : 'end') : 'center',
                gap: 3,
              }}
            >
              {!printMode && (
                <Flex>
                  <Tabs variant="unstyled" index={tabIndex}>
                    <TabList>
                      <Tab
                        isDisabled={
                          !isInCalculationState &&
                          offer.insurerId == Insurer.Kooperativa
                        }
                      >
                        <TabContent
                          label={settingTooltip}
                          data-tour={showTour ? 'quote-item-addons' : undefined}
                          isDisabled={
                            !isInCalculationState &&
                            offer.insurerId == Insurer.Kooperativa
                          }
                          disabledTooltip={
                            !isInCalculationState &&
                              offer.insurerId == Insurer.Kooperativa
                              ? 'Garantovanou nabídku od Kooperativa již není možné editovat'
                              : undefined
                          }
                          image={slidersDuotone}
                          onClick={() => {
                            handleTabsChange(
                              tabIndex == 0 ? summaryTabIndex : 0,
                            )
                          }}
                          activeImage={
                            tabIndex === 0 ? slidersDuotoneActive : undefined
                          }
                        />
                      </Tab>

                      {!isPublicPoleas &&
                        <Tab
                          isDisabled={
                            !isInCalculationState &&
                            offer.insurerId == Insurer.Kooperativa
                          }
                        >
                          <TabContent
                            label="Slevy"
                            isDisabled={
                              !isInCalculationState &&
                              offer.insurerId == Insurer.Kooperativa
                            }
                            disabledTooltip={
                              !isInCalculationState &&
                                offer.insurerId == Insurer.Kooperativa
                                ? 'Garantovanou nabídku od Kooperativa již není možné editovat'
                                : undefined
                            }
                            image={discountsImage}
                            onClick={() => {
                              handleTabsChange(
                                tabIndex == 1 ? summaryTabIndex : 1,
                              )
                            }}
                            activeImage={
                              tabIndex === 1 ? discountsActive : undefined
                            }
                            showErrorBadge={
                              discountsFormMethods.formState.errors &&
                              (discountsFormMethods.formState.errors.discounts
                                ?.length ?? 0) > 0
                            }
                          />
                        </Tab>
                      }
                    </TabList>
                  </Tabs>

                  <CustomTooltip label={<QuoteItemCkp offer={offer} />} trigger="clickHover">
                    <Image src={carBurstDuotone} alt={'ČKP'} py={2} px={3} />
                  </CustomTooltip>

                  {user?.enableCloneOffer && !printMode && (
                    <CustomTooltip label="Klonovat nabídku">

                      <Button
                        variant='ghost'
                        py={2}
                        px={3}
                        isDisabled={isDisabled}
                        cursor="pointer"
                        onClick={() => {
                          setLoading(true)

                          cloneOffer()
                            .then(({ data: offer }) => {
                              updateOffer(offer as OfferVehicle)
                              recalculateOffers([offer.offerId], true)
                            })
                            .finally(() => {
                              setLoading(false)
                            })
                        }}
                      >
                        <CloneDuotoneReact />
                      </Button>


                    </CustomTooltip>
                  )}
                </Flex>
              )}

              <CustomTooltip
                label={
                  offer.included
                    ? 'Vyřadit ze srovnání'
                    : 'Zahrnout do srovnání'
                }
              >
                <Box mr={3}>
                  <Switch
                    isChecked={offer.included}
                    isDisabled={printMode || disabledSwitchIncludeButton}
                    size="lg"
                    onChange={handleQuoteVisibleChange}
                    colorScheme='primary'
                  />
                </Box>
              </CustomTooltip>

              {/* dalsi item zde musi mit px={3} aby byly mezery stejne */}
            </Flex>
          </Grid>
        </CardHeader>

        <CardBody sx={{ p: 0 }}>
          <Grid sx={{ gridTemplateColumns: { md: '1fr auto' }, gap: 8 }}>
            <Tabs variant="unstyled" index={tabIndex} sx={{ height: '100%' }}>
              <TabPanels sx={{ height: '100%' }}>
                <TabPanel sx={{ p: { base: 4, md: 8 }, pr: { md: 0 }, pt: { md: 0 } }}>
                  {tabIndex === 0 && (
                    <QuoteItemSettingsForm
                      offer={offer}
                      addons={addons}
                      setAddons={updateAddons}
                    />
                  )}
                </TabPanel>

                <TabPanel sx={{ height: '100%', p: { base: 4, md: 8 }, pr: { md: 0 }, pt: { md: 0 } }}>
                  {tabIndex === 1 && (
                    <>
                      {isLoadingDiscounts && <Loading large={true} />}
                      {!isLoadingDiscounts && (
                        <QuoteItemDiscountsForm
                          offer={offer}
                          requestDiscountData={requestDiscountData}
                          // refreshOfferForm={refreshOfferForm}
                          formMethods={discountsFormMethods}
                        />
                      )}
                    </>
                  )}
                </TabPanel>

                <TabPanel
                  sx={{ p: { base: 4, md: 0 }, pb: 2, pl: { md: 8 } }}
                >
                  {tabIndex === summaryTabIndex && (
                    <QuoteSummary offer={offer} />
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>

            <QuoteItemSidebar
              currentStatus={currentStatus}
              onPaymentFrequencyChange={(val) => {
                if (val && val != offer.paymentFrequencyId)
                  handleStatusChange(OfferStatus.changed)

                setChangedPaymentFrequencyId(val)
              }}
              offer={offer}
              submitHandler={(force) => {
                if (force) submit()
                else {
                  discountsFormMethods.reset()
                  resetAddons()

                  handleStatusChange(OfferStatus.completed)
                }
              }}
              availableDiscounts={
                (discountsFormMethods
                  .getValues('discounts')
                  ?.filter((x) => x.editK2)?.length ?? 0) > 0
              }
            />
          </Grid>
        </CardBody>
      </Card >
    )
  },
)